import { useMemo, useState } from 'react';
import { ResponsiveContainer, Tooltip, Treemap } from 'recharts';
import { formatBytes } from '../api/client';
import type { SizeNode } from '../api/types';

// Categorical palette (navy-first); cells are colored by the top-level folder
// of their full path so colors stay stable across drill levels, WizTree-style.
const PALETTE = [
  '#282a67', '#0e7490', '#15803d', '#a16207', '#b91c1c', '#7e22ce',
  '#be185d', '#4d7c0f', '#1d4ed8', '#0f766e', '#86198f', '#b45309',
];

function colorFor(label: string): string {
  let h = 0;
  for (let i = 0; i < label.length; i++) h = (h * 31 + label.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length] ?? PALETTE[0]!;
}

interface CellDatum {
  name: string;
  path: string;
  size: number;
  count: number;
  kind: SizeNode['kind'];
  hasChildren: boolean;
  value: number;
}

interface CellProps extends Partial<CellDatum> {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  onDrill?: (path: string) => void;
}

/** Custom treemap cell: colored rect + name/size labels, click-to-drill on folders. */
function Cell(props: CellProps) {
  const { x = 0, y = 0, width = 0, height = 0, name = '', path = '', size = 0, kind, hasChildren, onDrill } = props;
  if (width <= 0 || height <= 0 || !name) return null;
  const topSegment = path.split('/')[0] || name;
  const fill = kind === 'other' ? '#9ca3af' : colorFor(topSegment);
  const drillable = kind === 'folder' && hasChildren;
  return (
    <g
      onClick={drillable && onDrill ? () => onDrill(path) : undefined}
      style={drillable ? { cursor: 'pointer' } : undefined}
    >
      <rect x={x} y={y} width={width} height={height} fill={fill} fillOpacity={kind === 'file' ? 0.6 : 0.9} stroke="#fff" />
      {width > 60 && height > 28 ? (
        <>
          <text x={x + 6} y={y + 16} fill="#fff" fontSize={12} fontWeight={600} pointerEvents="none">
            {name.length > Math.floor(width / 7) ? `${name.slice(0, Math.max(1, Math.floor(width / 7) - 1))}…` : name}
            {kind === 'folder' ? '/' : ''}
          </text>
          <text x={x + 6} y={y + 30} fill="#fff" fontSize={10} fillOpacity={0.85} pointerEvents="none">
            {formatBytes(size)}
          </text>
        </>
      ) : null}
    </g>
  );
}

function SizeTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: CellDatum }> }) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0]!.payload;
  return (
    <div className="rounded border border-gray-200 bg-white px-3 py-2 text-xs shadow">
      <div className="max-w-xs break-all font-mono">{d.path || d.name}</div>
      <div className="mt-1 font-semibold text-navy">{formatBytes(d.size)}</div>
      <div className="text-gray-500">
        {d.count.toLocaleString()} object{d.count === 1 ? '' : 's'}
        {d.kind === 'other' ? ' (smaller items, rolled up)' : ''}
      </div>
    </div>
  );
}

/** Walk the tree to the folder node whose path matches (path '' = root). */
function findNode(root: SizeNode, path: string): SizeNode | null {
  let node: SizeNode = root;
  while (node.path !== path) {
    const next = (node.children ?? []).find(
      (c) => c.kind === 'folder' && (c.path === path || path.startsWith(c.path)),
    );
    if (!next) return null;
    node = next;
  }
  return node;
}

/**
 * WizTree-style one-level treemap with drill-down: clicking a folder re-roots
 * the map into that subtree; the breadcrumb navigates back up.
 */
export function SizeTreemap({ root }: { root: SizeNode }) {
  const [rootPath, setRootPath] = useState('');
  const node = findNode(root, rootPath) ?? root;

  const data: CellDatum[] = useMemo(
    () =>
      (node.children ?? []).map((c) => ({
        name: c.name,
        path: c.path,
        size: c.size,
        count: c.count,
        kind: c.kind,
        hasChildren: (c.children?.length ?? 0) > 0,
        value: c.size,
      })),
    [node],
  );

  const crumbs = rootPath.split('/').filter(Boolean);

  return (
    <div>
      <div className="mb-2 text-sm text-gray-600">
        <button className="text-navy hover:underline" onClick={() => setRootPath('')}>
          root
        </button>
        {crumbs.map((part, i) => {
          const next = crumbs.slice(0, i + 1).join('/') + '/';
          return (
            <span key={next}>
              {' / '}
              <button className="text-navy hover:underline" onClick={() => setRootPath(next)}>
                {part}
              </button>
            </span>
          );
        })}
        <span className="ml-2 text-xs text-gray-400">
          {formatBytes(node.size)} · {node.count.toLocaleString()} objects
        </span>
      </div>
      {data.length === 0 ? (
        <p className="py-10 text-center text-sm text-gray-400">
          No drill-down data here{node.count > 0 ? ' (contents were aggregated)' : ''}.
        </p>
      ) : (
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <Treemap
              data={data}
              dataKey="value"
              isAnimationActive={false}
              content={<Cell onDrill={setRootPath} />}
            >
              <Tooltip content={<SizeTooltip />} />
            </Treemap>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
